import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { paymentService } from '../../services/payment.service';
import { invoiceService } from '../../services/invoice.service';
import { PaymentMethod, InvoiceStatus } from '../../types';

const schema = z.object({
  invoiceId: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().min(1, 'Invoice is required')
  ),
  paymentDate: z.string().min(1, 'Payment date is required'),
  amount: z.preprocess(
    (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
    z.number().positive('Amount must be positive')
  ),
  paymentMethod: z.nativeEnum(PaymentMethod),
  transactionReference: z.string().optional(),
  payerName: z.string().min(1, 'Payer name is required'),
  payerContact: z.string().optional(),
  notes: z.string().optional(),
});

type FormInput = z.infer<typeof schema>;

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();

  const { data: invoices = [] } = useQuery({
    queryKey: ['invoices'],
    queryFn: invoiceService.getAll,
    enabled: isOpen,
  });

  const issuedInvoices = invoices.filter(
    (inv) => inv.status === InvoiceStatus.ISSUED || inv.status === InvoiceStatus.DRAFT
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
  });

  const selectedInvoiceId = watch('invoiceId');
  const selectedInvoice = invoices.find((inv) => inv.id === Number(selectedInvoiceId));

  const mutation = useMutation({
    mutationFn: paymentService.record,
    onSuccess: () => {
      toast.success('Payment recorded successfully');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      reset();
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to record payment');
    },
  });

  const onSubmit = (data: FormInput) => {
    const parsed = schema.parse(data);
    mutation.mutate(parsed);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Record Payment"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            Record
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Select
          label="Invoice"
          required
          error={errors.invoiceId?.message}
          options={[
            { value: '', label: 'Select an invoice' },
            ...issuedInvoices.map((inv) => ({
              value: String(inv.id),
              label: `${inv.invoiceNumber} - ${inv.clientName} (${new Intl.NumberFormat('en-TZ', {
                style: 'currency',
                currency: 'TZS',
                minimumFractionDigits: 0,
              }).format(inv.totalAmount)})`,
            })),
          ]}
          {...register('invoiceId')}
        />

        {selectedInvoice && (
          <div className="bg-blue-50 p-3 rounded-lg text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Invoice Amount:</span>
              <span className="font-semibold">
                {new Intl.NumberFormat('en-TZ', {
                  style: 'currency',
                  currency: 'TZS',
                  minimumFractionDigits: 0,
                }).format(selectedInvoice.totalAmount)}
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-600">Client:</span>
              <span className="font-medium">{selectedInvoice.clientName}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Payment Date"
            type="date"
            error={errors.paymentDate?.message}
            required
            {...register('paymentDate')}
          />

          <Input
            label="Amount (TZS)"
            type="number"
            placeholder="e.g., 1405000"
            error={errors.amount?.message}
            required
            {...register('amount')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Payment Method"
            required
            error={errors.paymentMethod?.message}
            options={[
              { value: '', label: 'Select payment method' },
              { value: PaymentMethod.CASH, label: 'Cash' },
              { value: PaymentMethod.BANK_TRANSFER, label: 'Bank Transfer' },
              { value: PaymentMethod.MOBILE_MONEY, label: 'Mobile Money' },
              { value: PaymentMethod.CHEQUE, label: 'Cheque' },
              { value: PaymentMethod.CREDIT_CARD, label: 'Credit Card' },
              { value: PaymentMethod.OTHER, label: 'Other' },
            ]}
            {...register('paymentMethod')}
          />

          <Input
            label="Transaction Reference"
            placeholder="e.g., TXN-20240127-001"
            error={errors.transactionReference?.message}
            {...register('transactionReference')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Payer Name"
            placeholder="e.g., ABC Company Ltd"
            error={errors.payerName?.message}
            required
            {...register('payerName')}
          />

          <Input
            label="Payer Contact"
            placeholder="e.g., +255712345678"
            error={errors.payerContact?.message}
            {...register('payerContact')}
          />
        </div>

        <Input
          label="Notes"
          placeholder="e.g., Payment received via bank transfer"
          error={errors.notes?.message}
          {...register('notes')}
        />
      </form>
    </Modal>
  );
};
